import React, { useState, useEffect } from "react";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  updateAnnouncement,
  getAnnouncementById,
  approveAnnouncement,
  rejectAnnouncement,
  UploadFeatureImageUrl,
  UploadBlogBodyImage,
} from "proxyUrl";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { BsFillImageFill } from "react-icons/bs";
import { useSelector } from "react-redux";

const AnnouncementEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // Permissions
  const loggedUser = useSelector((state) => state.user.user || {});
  const userPermissions = loggedUser.userPermission || [];
  const canUpdateAnnouncement = userPermissions.includes("update announcement");

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [announcementType, setAnnouncementType] = useState("General");
  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState(null);
  const [oldImage, setOldImage] = useState(null);
  const [status, setStatus] = useState("pending");
  const [notification, setNotification] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [canApprove, setCanApprove] = useState(false);
  const [processingAction, setProcessingAction] = useState(false);

  const fetchAnnouncementData = async () => {
    try {
      const response = await axios.get(`${getAnnouncementById}${id}`, {
        withCredentials: true,
      });

      if (response.data && response.data.data) {
        const data = response.data.data;
        setTitle(data.title || "");
        setDescription(data.description || "");
        setAnnouncementType(data.announcementType || "General");
        setStatus(data.status || "pending");
        setNotification(data.notification || 0);
        setOldImage(data.image || null);
        // Get canApprove from API response
        setCanApprove(response.data.canApprove || false);
      }
    } catch (error) {
      console.error("Error fetching announcement:", error);
      toast.error("Failed to fetch announcement data", {
        position: "top-center",
        autoClose: 2000,
      });
      navigate("/admin/announcements");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnnouncementData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleImage = (e) => {
    const selectedFile = e.target.files[0];
    setImage(selectedFile);
    if (selectedFile) {
      setPreview(URL.createObjectURL(selectedFile));
    }
  };

  const handleApprove = async () => {
    if (!window.confirm("Are you sure you want to approve this announcement?")) {
      return;
    }

    setProcessingAction(true);
    try {
      const response = await axios.post(
        `${approveAnnouncement}${id}/approve`,
        {},
        {
          withCredentials: true,
        }
      );

      if (response.status === 200) {
        toast.success("Announcement approved successfully!", {
          position: "top-center",
          autoClose: 2000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });

        // Navigate to main listing page after approval
        const delayNavigate = () => {
          navigate("/admin/announcements");
        };
        setTimeout(delayNavigate, 2100);
      }
    } catch (error) {
      console.error("Error approving announcement:", error);
      toast.error(
        error.response?.data?.error || "Failed to approve announcement!",
        {
          position: "top-center",
          autoClose: 2000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        }
      );
      setProcessingAction(false);
    }
  };

  const handleReject = async () => {
    const rejectionReason = prompt("Please enter rejection reason:");
    if (!rejectionReason || rejectionReason.trim() === "") {
      toast.error("Rejection reason is required!", {
        position: "top-center",
        autoClose: 2000,
      });
      return;
    }

    setProcessingAction(true);
    try {
      const response = await axios.post(
        `${rejectAnnouncement}${id}/reject`,
        { rejectionReason: rejectionReason.trim() },
        {
          withCredentials: true,
        }
      );

      if (response.status === 200) {
        toast.success("Announcement rejected successfully!", {
          position: "top-center",
          autoClose: 2000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });

        // Navigate to main listing page after rejection
        const delayNavigate = () => {
          navigate("/admin/announcements");
        };
        setTimeout(delayNavigate, 2100);
      }
    } catch (error) {
      console.error("Error rejecting announcement:", error);
      toast.error(
        error.response?.data?.error || "Failed to reject announcement!",
        {
          position: "top-center",
          autoClose: 2000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        }
      );
      setProcessingAction(false);
    }
  };

  if (!canUpdateAnnouncement) {
    return (
      <div className="flex h-64 items-center justify-center text-sm text-gray-600">
        You are not authorized to update announcements.
      </div>
    );
  }

  const sendData = async (e) => {
    e.preventDefault();

    if (!canUpdateAnnouncement) {
      toast.error("You do not have permission to update announcements");
      return;
    }

    // Validate required fields
    if (!title || !description) {
      toast.error("Title and description are required!", {
        position: "top-center",
        autoClose: 2000,
      });
      return;
    }

    // Validate title length
    if (title.length > 120) {
      toast.error("Title must be 120 characters or less!", {
        position: "top-center",
        autoClose: 2000,
      });
      return;
    }

    setSubmitted(true);

    try {
      let imageUrl = oldImage;

      // Upload new image if provided
      if (image) {
        const formData = new FormData();
        formData.append("image", image);

        const uploadResponse = await axios.post(UploadFeatureImageUrl, formData, {
          withCredentials: true,
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        if (uploadResponse.status === 200) {
          imageUrl = uploadResponse.data.imageUrl;
        }
      }

      // Update announcement
      const announcementData = {
        title: title.trim(),
        description: description,
        announcementType: announcementType,
        image: imageUrl,
        status: status,
        notification: parseInt(notification),
      };

      const response = await axios.put(
        `${updateAnnouncement}${id}`,
        announcementData,
        {
          withCredentials: true,
        }
      );

      if (response.status === 200) {
        toast.success("Announcement updated successfully!", {
          position: "top-center",
          autoClose: 2000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });

        // Refresh the announcement data to stay on the same page
        await fetchAnnouncementData();
        setSubmitted(false);
      }
    } catch (error) {
      console.error("Error updating announcement:", error);
      toast.error(
        error.response?.data?.error || "Failed to update announcement!",
        {
          position: "top-center",
          autoClose: 2000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        }
      );
      setSubmitted(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-10">
        <div className="text-gray-500">Loading announcement data...</div>
      </div>
    );
  }

  return (
    <>
      <div className="mt-7 mb-2 rounded-lg bg-white shadow-xl dark:bg-navy-800">
        <div className="mx-auto w-[90%] py-2">
          <form action="" method="POST" className="">
            <div className="">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                <div className="input relative">
                  <label htmlFor="title">Title *</label>
                  <input
                    type="text"
                    placeholder="Enter announcement title"
                    maxLength={120}
                    name="title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    required
                  />
                  <span className="text-xs text-gray-500">
                    {title.length}/120 characters
                  </span>
                </div>
                <div className="input relative">
                  <label htmlFor="announcementType">Announcement Type *</label>
                  <select
                    id="announcementType"
                    value={announcementType}
                    onChange={(e) => setAnnouncementType(e.target.value)}
                    required
                  >
                    <option value="General">General</option>
                    <option value="Event">Event</option>
                  </select>
                </div>
                <div className="input relative">
                  <label htmlFor="status">Status</label>
                  <select
                    id="status"
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                  >
                    <option value="draft">Draft</option>
                    <option value="pending">Pending</option>
                    {/* <option value="approved">Approved</option> */}
                  </select>
                </div>
                <div className="input relative">
                  <label htmlFor="notification">Notification Target</label>
                  <select
                    id="notification"
                    value={notification}
                    onChange={(e) => setNotification(e.target.value)}
                  >
                    <option value={0}>All</option>
                    <option value={1}>VL</option>
                    <option value={2}>Vnaya</option>
                    <option value={3}>Creed</option>
                    <option value={4}>ASI</option>
                  </select>
                </div>
              </div>

              <div className="field my-6">
                <div className="input relative mx-2 mr-7 flex items-center justify-center rounded-lg border-2 border-blueSecondary">
                  <div className="h-[200px] w-[400px] rounded-lg">
                    {preview ? (
                      <img
                        className="h-full w-full rounded-md object-cover"
                        src={preview}
                        alt="Preview"
                      />
                    ) : oldImage ? (
                      <img
                        className="h-full w-full rounded-md object-cover"
                        src={oldImage}
                        alt="Current"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-8xl text-indigo-600">
                        <BsFillImageFill />
                      </div>
                    )}
                  </div>
                </div>
                <div className="input relative">
                  <label htmlFor="image">Upload New Image (optional)</label>
                  <input
                    type="file"
                    name="image"
                    accept="image/*"
                    onChange={handleImage}
                  />
                </div>
              </div>

              <div className="relative mx-2">
                <h2 className="mb-1">Description *</h2>
                <CKEditor
                  editor={ClassicEditor}
                  data={description}
                  onReady={(editor) => {
                    // Editor is ready to use
                  }}
                  onChange={(event, editor) => {
                    const data = editor.getData();
                    setDescription(data);
                  }}
                  config={{
                    ckfinder: {
                      uploadUrl: `${UploadBlogBodyImage}`,
                      withCredentials: true,
                    },
                  }}
                />
              </div>

              {/* Action Buttons - Update, Approve, Reject side by side */}
              {submitted || processingAction ? (
                <div className="field my-5 mb-8 flex gap-4">
                  <div className="flex-1 rounded-md bg-gray-300 py-3 px-6 text-center text-white">
                    <button disabled>Loading....</button>
                  </div>
                  {canApprove && status === "pending" && (
                    <>
                      <div className="flex-1 rounded-md bg-gray-300 py-3 px-6 text-center text-white">
                        <button disabled>Processing...</button>
                      </div>
                      <div className="flex-1 rounded-md bg-gray-300 py-3 px-6 text-center text-white">
                        <button disabled>Processing...</button>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <div className="field my-5 mb-8 flex gap-4">
                  <button
                    type="button"
                    onClick={sendData}
                    className="flex-1 rounded-md bg-indigo-600 py-3 px-6 text-white hover:bg-indigo-600 transition-colors duration-200"
                  >
                    Update
                  </button>
                  {canApprove && status === "pending" && (
                    <>
                      <button
                        type="button"
                        onClick={handleApprove}
                        className="flex-1 rounded-md bg-green-500 py-3 px-6 text-white hover:bg-green-600 transition-colors duration-200"
                      >
                        Approve
                      </button>
                      <button
                        type="button"
                        onClick={handleReject}
                        className="flex-1 rounded-md bg-red-500 py-3 px-6 text-white hover:bg-red-600 transition-colors duration-200"
                      >
                        Reject
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </form>
        </div>
      </div>
      <ToastContainer />
    </>
  );
};

export default AnnouncementEdit;

